/**
 * ComponentBreakdown - shows exactly how the Resume Alignment Score was
 * built from the five spec components:
 *   Required Skills 30 · Relevant Experience 25 · Responsibilities 20 ·
 *   Qualifications 15 · ATS/Keywords 10.
 *
 * Every row shows the score/max, a horizontal fill bar in a category-
 * appropriate colour, and a tiny label. Total row confirms the components
 * add up to the displayed final score - the auditability the deterministic
 * scorer now guarantees.
 *
 * Rendered as a no-op if `components` is missing (older analyses saved
 * before the deterministic scorer shipped).
 */
export default function ComponentBreakdown({ components, maxes, total }) {
  if (!components || !maxes) return null;
  const rows = [
    { key: "required_skills",     label: "Required Skills",     hint: "JD's must-have skills matched against real resume evidence." },
    { key: "relevant_experience", label: "Relevant Experience", hint: "Years, seniority, scope, and day-to-day functions vs the JD." },
    { key: "responsibilities",    label: "Responsibilities",    hint: "Major JD responsibilities that your resume demonstrably shows." },
    { key: "qualifications",      label: "Qualifications",      hint: "Required degrees, certifications, and licenses." },
    { key: "ats_keywords",        label: "ATS / Keywords",      hint: "Industry terminology and JD keywords represented in the resume." },
  ];
  return (
    <div data-testid="component-breakdown" className="rounded-xl border border-zinc-800/70 bg-zinc-950/40 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold">
          Alignment Breakdown
        </div>
        <div className="text-[11px] text-zinc-500">
          Evidence-based breakdown · {maxes && maxes.qualifications === 0 ? "scaled to" : "components sum to"} <span className="text-zinc-300 tabular-nums font-semibold">{total}</span>/100
        </div>
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => {
          const val = components[r.key];
          const max = maxes[r.key];
          if (val == null && max === 0) {
            return (
              <div key={r.key} data-testid={`component-row-${r.key}`} className="flex items-baseline justify-between">
                <span className="text-xs text-zinc-400 font-medium">{r.label}</span>
                <span className="text-[10px] text-zinc-500 tabular-nums">N/A — not required for this role</span>
              </div>
            );
          }
          if (val == null || max == null) return null;
          const pct = Math.max(0, Math.min(100, (val / max) * 100));
          const tone =
            pct >= 90 ? "emerald" :
            pct >= 65 ? "amber"   :
            pct >= 35 ? "orange"  : "rose";
          const barColor = {
            emerald: "from-emerald-500/60 to-emerald-500/25",
            amber:   "from-amber-500/60 to-amber-500/25",
            orange:  "from-orange-500/60 to-orange-500/25",
            rose:    "from-rose-500/60 to-rose-500/20",
          }[tone];
          const textColor = {
            emerald: "text-emerald-300",
            amber:   "text-amber-300",
            orange:  "text-orange-300",
            rose:    "text-rose-300",
          }[tone];
          return (
            <div
              key={r.key}
              data-testid={`component-row-${r.key}`}
              className="grid grid-cols-[minmax(0,1fr)_60px] items-center gap-3"
            >
              <div>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-xs text-zinc-200 font-medium">{r.label}</span>
                  <span className={`text-[10px] ${textColor} tabular-nums font-semibold`}>
                    {val.toFixed(1)} / {max}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-zinc-900 overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${barColor}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-[10px] text-zinc-500 mt-1 leading-snug">{r.hint}</div>
              </div>
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">%</div>
                <div className={`text-sm tabular-nums font-semibold ${textColor}`}>{Math.round(pct)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-800/70 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">Total</span>
        <span className="text-sm tabular-nums font-semibold text-zinc-100">
          <span data-testid="component-breakdown-total">{total}</span> / 100
        </span>
      </div>
    </div>
  );
}
